// ============================================================
// THE COMPANY — Main Org Chart Canvas
// ============================================================

import { useMemo, useCallback } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  useReactFlow,
  type Node,
  type Edge,
  type OnNodeDrag,
  type NodeMouseHandler,
  type OnConnect,
  BackgroundVariant,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import RoleNode from "./RoleNode";
import DependencyEdge from "./DependencyEdge";
import { useCompanyStore } from "../../store/useCompanyStore";
import { getLayoutedElements } from "../../lib/layout";
import type { Role, RoleNodeData } from "../../lib/types";

// ── Custom type registrations ────────────────────────────────

const nodeTypes = { roleNode: RoleNode };
const edgeTypes = { dependency: DependencyEdge };

// ── Helper: check hierarchy ancestry ─────────────────────────

function isDescendantOf(
  nodeId: string,
  potentialAncestorId: string,
  roles: Role[],
): boolean {
  const roleMap = new Map(roles.map((r) => [r.id, r]));
  const visited = new Set<string>();
  let current = roleMap.get(nodeId);
  while (current) {
    if (visited.has(current.id)) return false; // prevent infinite loop on circular parentId
    visited.add(current.id);
    if (current.parentId === potentialAncestorId) return true;
    current = current.parentId ? roleMap.get(current.parentId) : undefined;
  }
  return false;
}

// ── Inner chart (needs ReactFlowProvider ancestor) ───────────

function OrgChartInner() {
  const roles = useCompanyStore((s) => s.roles);
  const dependencies = useCompanyStore((s) => s.dependencies);
  const conversations = useCompanyStore((s) => s.conversations);
  const actionItems = useCompanyStore((s) => s.actionItems);
  const selectRole = useCompanyStore((s) => s.selectRole);
  const reparentRole = useCompanyStore((s) => s.reparentRole);
  const addDependency = useCompanyStore((s) => s.addDependency);

  const { getIntersectingNodes } = useReactFlow();

  // Build conversation count lookup
  const conversationCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const [roleId, convos] of Object.entries(conversations)) {
      counts[roleId] = convos.length;
    }
    return counts;
  }, [conversations]);

  const searchQuery = useCompanyStore((s) => s.searchQuery);

  // Compute dagre layout
  const { nodes, edges } = useMemo(() => {
    const layout = getLayoutedElements(roles, dependencies, conversationCounts);

    // Remap dependency edges to use our custom edge type
    const remappedEdges: Edge[] = layout.edges.map((edge) => {
      if (edge.id.startsWith("dep-")) {
        return { ...edge, type: "dependency" };
      }
      return edge;
    });

    // Compute last conversation time per role
    const lastConvoAt: Record<string, string | null> = {};
    for (const [roleId, convos] of Object.entries(conversations)) {
      if (convos.length > 0) {
        lastConvoAt[roleId] = convos[convos.length - 1].createdAt;
      }
    }

    // Apply search dimming + last conversation + action item counts
    const q = searchQuery.trim().toLowerCase();
    const nodesWithExtras = layout.nodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        lastConversationAt: lastConvoAt[node.id] ?? null,
        pendingActionItems: (actionItems[node.id] ?? []).filter(
          (i) => !i.completed,
        ).length,
        dimmed:
          q.length > 0 &&
          !node.data.role.name.toLowerCase().includes(q) &&
          !node.data.role.title.toLowerCase().includes(q),
      },
    }));

    return { nodes: nodesWithExtras, edges: remappedEdges };
  }, [
    roles,
    dependencies,
    conversationCounts,
    conversations,
    actionItems,
    searchQuery,
  ]);

  // ── Drag-drop reparenting ──────────────────────────────────

  const onNodeDragStop: OnNodeDrag<Node<RoleNodeData>> = useCallback(
    (_event, draggedNode) => {
      const intersecting = getIntersectingNodes(draggedNode);
      if (intersecting.length === 0) return;

      // Pick the first valid drop target
      const target = intersecting.find((n) => {
        if (n.id === draggedNode.id) return false;
        // Can't drop onto own descendant (would create cycle)
        if (isDescendantOf(n.id, draggedNode.id, roles)) return false;
        return true;
      });

      if (target) {
        reparentRole(draggedNode.id, target.id).catch((err) => {
          console.error("Failed to reparent role:", err);
        });
      }
    },
    [getIntersectingNodes, roles, reparentRole],
  );

  // ── Node click → select role ───────────────────────────────

  const onNodeClick: NodeMouseHandler<Node<RoleNodeData>> = useCallback(
    (_event, node) => {
      selectRole(node.id);
    },
    [selectRole],
  );

  // ── Connect handler → create dependency edge ───────────────

  const onConnect: OnConnect = useCallback(
    (connection) => {
      if (connection.source && connection.target) {
        addDependency({
          sourceId: connection.source,
          targetId: connection.target,
        });
      }
    },
    [addDependency],
  );

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      onNodeDragStop={onNodeDragStop}
      onNodeClick={onNodeClick}
      onConnect={onConnect}
      fitView
      proOptions={{ hideAttribution: true }}
      minZoom={0.2}
      maxZoom={2}
      defaultEdgeOptions={{ animated: false }}
      style={{ backgroundColor: "#0a0a0f" }}
    >
      <Background
        variant={BackgroundVariant.Dots}
        gap={20}
        size={1}
        color="#1e1e2e"
      />
      <Controls
        showInteractive={false}
        style={{
          backgroundColor: "#111118",
          border: "1px solid #2a2a3a",
          borderRadius: 8,
        }}
      />
      <MiniMap
        nodeColor={() => "#2a2a3a"}
        maskColor="rgba(10, 10, 15, 0.85)"
        style={{
          backgroundColor: "#111118",
          border: "1px solid #2a2a3a",
          borderRadius: 8,
        }}
      />
    </ReactFlow>
  );
}

// ── Exported wrapper with provider ───────────────────────────

export default function OrgChart() {
  return (
    <ReactFlowProvider>
      <OrgChartInner />
    </ReactFlowProvider>
  );
}
